<template>
    <v-form ref="formRef" @submit.prevent="onSubmit">
        <v-row>
            <template v-if="entryType === 'ware'">
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model="formData.car_number"
                        :label="$t('reservation.carNumber')"
                        variant="outlined"
                        hide-details="auto"
                        :rules="[requiredRule]"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model="formData.driver_name"
                        :label="$t('reservation.driverName')"
                        variant="outlined"
                        hide-details="auto"
                        :rules="[requiredRule]"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model="formData.driver_surname"
                        :label="$t('reservation.driverSurname')"
                        variant="outlined"
                        hide-details="auto"
                        :rules="[requiredRule]"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model="formData.customs"
                        :label="$t('reservation.customs')"
                        variant="outlined"
                        hide-details="auto"
                    />
                </v-col>
            </template>
            <v-col cols="12">
                <v-list-subheader class="px-0">{{ $t('reservation.loadingSection') }}</v-list-subheader>
            </v-col>
            <v-col cols="12" md="6">
                <v-tooltip
                    :text="loadingConstraints.dateDisabledTooltip"
                    location="top"
                    :disabled="!loadingConstraints.dateDisabled"
                >
                    <template #activator="{ props }">
                        <div v-bind="props">
                            <FormDate
                                ref="formLoadingDateRef"
                                v-model="loadingDateTimeRange"
                                data-cy-prefix="reservation-loading"
                                :min="loadingConstraints.dateMin"
                                :max="loadingConstraints.dateMax"
                                :disabled="loadingConstraints.dateDisabled"
                                single
                            />
                        </div>
                    </template>
                </v-tooltip>
            </v-col>
            <v-col cols="12" md="6">
                <v-tooltip
                    :text="loadingConstraints.timeDisabledTooltip"
                    location="top"
                    :disabled="!loadingConstraints.timeDisabled"
                >
                    <template #activator="{ props }">
                        <div v-bind="props">
                            <FormTime
                                ref="formLoadingTimeRef"
                                v-model="loadingDateTimeRange"
                                data-cy-prefix="reservation-loading"
                                :min="loadingConstraints.timeMin"
                                :max="loadingConstraints.timeMax"
                                :disabled="loadingConstraints.timeDisabled"
                                required
                            />
                        </div>
                    </template>
                </v-tooltip>
            </v-col>
            <v-col cols="12">
                <v-list-subheader class="px-0">{{ $t('reservation.unloadingSection') }}</v-list-subheader>
            </v-col>
            <v-col cols="12" md="6">
                <v-tooltip
                    :text="unloadingConstraints.dateDisabledTooltip"
                    location="top"
                    :disabled="!unloadingConstraints.dateDisabled"
                >
                    <template #activator="{ props }">
                        <div v-bind="props">
                            <FormDate
                                ref="formUnloadingDateRef"
                                v-model="unloadingDateTimeRange"
                                data-cy-prefix="reservation-unloading"
                                :min="unloadingConstraints.dateMin"
                                :max="unloadingConstraints.dateMax"
                                :disabled="unloadingConstraints.dateDisabled"
                                single
                            />
                        </div>
                    </template>
                </v-tooltip>
            </v-col>
            <v-col cols="12" md="6">
                <v-tooltip
                    :text="unloadingConstraints.timeDisabledTooltip"
                    location="top"
                    :disabled="!unloadingConstraints.timeDisabled"
                >
                    <template #activator="{ props }">
                        <div v-bind="props">
                            <FormTime
                                ref="formUnloadingTimeRef"
                                v-model="unloadingDateTimeRange"
                                data-cy-prefix="reservation-unloading"
                                :min="unloadingConstraints.timeMin"
                                :max="unloadingConstraints.timeMax"
                                :disabled="unloadingConstraints.timeDisabled"
                                required
                            />
                        </div>
                    </template>
                </v-tooltip>
            </v-col>
            <template v-if="entryType === 'truck'">
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model.number="formData.cargo_weight"
                        :label="$t('reservation.cargoWeight')"
                        type="number"
                        step="0.01"
                        variant="outlined"
                        hide-details="auto"
                    />
                </v-col>
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model.number="formData.cargo_mdp"
                        :label="$t('reservation.cargoMdp')"
                        type="number"
                        step="0.01"
                        variant="outlined"
                        hide-details="auto"
                    />
                </v-col>
                <v-col cols="12">
                    <v-textarea
                        v-model="formData.special_transport_notes"
                        :label="$t('reservation.specialTransportNotes')"
                        variant="outlined"
                        rows="2"
                        hide-details="auto"
                    />
                </v-col>
            </template>
        </v-row>
        <v-card-actions class="px-0 pb-0 pt-4">
            <v-row dense class="w-100 ma-0">
                <v-col cols="6" class="pa-1">
                    <v-btn block variant="flat" color="error" @click="$emit('cancel')">
                        {{ $t('reservation.cancel') }}
                    </v-btn>
                </v-col>
                <v-col cols="6" class="pa-1">
                    <v-btn block variant="flat" color="primary" type="submit" :loading="submitting">
                        {{ $t('reservation.submit') }}
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-actions>
    </v-form>
</template>

<script>
import FormDate from "@/components/forms/FormDate.vue";
import FormTime from "@/components/forms/FormTime.vue";
import { formatToYmd, formatToHhMm } from "@/utils";

export default {
    components: { FormDate, FormTime },
    props: {
        entryType: {
            type: String,
            required: true,
            validator: (value) => ["truck", "ware"].includes(value),
        },
        loadingLocation: {
            type: Object,
            default: null,
        },
        unloadingLocation: {
            type: Object,
            default: null,
        },
    },
    emits: ["submit", "cancel"],
    data() {
        return {
            submitting: false,
            formData: {
                car_number: "",
                driver_name: "",
                driver_surname: "",
                customs: "",
                cargo_weight: null,
                cargo_mdp: null,
                special_transport_notes: "",
            },
            loadingDateTimeRange: {
                date: [],
                dateStart: "",
                dateEnd: "",
                timeStart: "",
                timeEnd: "",
            },
            unloadingDateTimeRange: {
                date: [],
                dateStart: "",
                dateEnd: "",
                timeStart: "",
                timeEnd: "",
            },
            requiredRule: (value) => !!value || this.$t("validation.required"),
        };
    },
    computed: {
        loadingConstraints() {
            return this._constraintsFromLocation(this.loadingLocation);
        },
        unloadingConstraints() {
            return this._constraintsFromLocation(this.unloadingLocation);
        },
    },
    watch: {
        loadingConstraints: {
            handler(constraints) {
                if (!constraints.dateMin) return;
                const dateObject = new Date(constraints.dateMin + "T12:00:00");
                let nextRange = {
                    ...this.loadingDateTimeRange,
                    date: [dateObject],
                    dateStart: constraints.dateMin,
                    dateEnd: constraints.dateMax,
                };
                if (constraints.timeDisabled && constraints.timeMin) {
                    nextRange = { ...nextRange, timeStart: constraints.timeMin, timeEnd: constraints.timeMax };
                }
                this.loadingDateTimeRange = nextRange;
            },
            immediate: true,
        },
        unloadingConstraints: {
            handler(constraints) {
                if (!constraints.dateMin) return;
                const dateObject = new Date(constraints.dateMin + "T12:00:00");
                let nextRange = {
                    ...this.unloadingDateTimeRange,
                    date: [dateObject],
                    dateStart: constraints.dateMin,
                    dateEnd: constraints.dateMax,
                };
                if (constraints.timeDisabled && constraints.timeMin) {
                    nextRange = { ...nextRange, timeStart: constraints.timeMin, timeEnd: constraints.timeMax };
                }
                this.unloadingDateTimeRange = nextRange;
            },
            immediate: true,
        },
    },
    methods: {
        _constraintsFromLocation(location) {
            const result = {
                dateMin: null,
                dateMax: null,
                dateDisabled: false,
                dateDisabledTooltip: "",
                timeMin: null,
                timeMax: null,
                timeDisabled: false,
                timeDisabledTooltip: "",
            };
            if (!location || !location.date_start || !location.date_end) return result;

            const dateStart = formatToYmd(location.date_start);
            const dateEnd = formatToYmd(location.date_end);
            if (!dateStart || !dateEnd) return result;

            result.dateMin = dateStart;
            result.dateMax = dateEnd;
            if (dateStart === dateEnd) {
                result.dateDisabled = true;
                result.dateDisabledTooltip = this.$t("reservation.singleDateTooltip");
            }

            const timeStart = formatToHhMm(location.time_start);
            const timeEnd = formatToHhMm(location.time_end);
            if (timeStart || timeEnd) {
                if (timeStart && timeEnd && timeStart === timeEnd) {
                    result.timeMin = timeStart;
                    result.timeMax = timeEnd;
                    result.timeDisabled = true;
                    result.timeDisabledTooltip = this.$t("reservation.singleTimeTooltip");
                } else {
                    result.timeMin = timeStart || "00:00";
                    result.timeMax = timeEnd || "23:59";
                }
            }
            return result;
        },
        async onSubmit() {
            const loadingDateValid = await this.$refs.formLoadingDateRef?.validateFields?.();
            const unloadingDateValid = await this.$refs.formUnloadingDateRef?.validateFields?.();
            const formValid = await this.$refs.formRef.validate();
            if (!formValid?.valid || !loadingDateValid || !unloadingDateValid) return;

            const load = this.loadingDateTimeRange;
            const unload = this.unloadingDateTimeRange;
            if (!load.dateStart || !load.dateEnd || !load.timeStart || !load.timeEnd) return;
            if (!unload.dateStart || !unload.dateEnd || !unload.timeStart || !unload.timeEnd) return;

            const timeStr = (t) => (typeof t === "string" ? t : "00:00").substring(0, 5);

            const payload = {
                car_number: this.entryType === "ware" ? this.formData.car_number : "",
                driver_name: this.entryType === "ware" ? this.formData.driver_name : "",
                driver_surname: this.entryType === "ware" ? this.formData.driver_surname : "",
                customs: this.entryType === "ware" ? (this.formData.customs || "").trim() : "",
                chosen_loading_date: load.dateStart,
                chosen_loading_time_start: timeStr(load.timeStart),
                chosen_loading_time_end: timeStr(load.timeEnd),
                chosen_unloading_date: unload.dateStart,
                chosen_unloading_time_start: timeStr(unload.timeStart),
                chosen_unloading_time_end: timeStr(unload.timeEnd),
            };

            if (this.entryType === "truck") {
                payload.cargo_weight = this.formData.cargo_weight ?? null;
                payload.cargo_mdp = this.formData.cargo_mdp ?? null;
                payload.special_transport_notes =
                    this.formData.special_transport_notes || null;
            }

            this.$emit("submit", payload);
        },
        setSubmitting(value) {
            this.submitting = value;
        },
    },
};
</script>
